import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';

const DOCUMENT_CONFIG = {
  privacy: {
    file: 'privacy-policy.txt',
    titles: { en: 'Privacy Policy', tr: 'Gizlilik Politikası' },
    path: 'privacy-policy',
  },
  terms: {
    file: 'terms-of-service.txt',
    titles: { en: 'Terms of Service', tr: 'Hizmet Şartları' },
    path: 'terms-of-service',
  },
};

function LegalDocumentPage({ type }) {
  const { lang } = useParams();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const config = DOCUMENT_CONFIG[type];
  const isValidLang = lang === 'en' || lang === 'tr';

  useEffect(() => {
    if (!isValidLang || !config) return;

    const title = config.titles[lang];
    document.title = `GymGM - ${title}`;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`/policies/${lang}/${config.file}`);
        if (!response.ok) throw new Error('Failed to load document');
        const text = await response.text();
        if (!cancelled) setContent(text);
      } catch (err) {
        console.error('Error loading legal document:', err);
        if (!cancelled) {
          setError(
            lang === 'tr'
              ? 'Belge yüklenemedi. Lütfen daha sonra tekrar deneyin.'
              : 'Could not load this document. Please try again later.'
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lang, type, isValidLang, config]);

  if (!config) {
    return <Navigate to="/" replace />;
  }

  if (!isValidLang) {
    return <Navigate to={`/${config.path}/en`} replace />;
  }

  const title = config.titles[lang];
  const otherLang = lang === 'en' ? 'tr' : 'en';
  const otherLabel = lang === 'en' ? 'Türkçe' : 'English';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        padding: '40px 20px 60px',
      }}
    >
      <article
        style={{
          maxWidth: 800,
          margin: '0 auto',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
          padding: '40px 32px',
          boxSizing: 'border-box',
        }}
      >
        <header
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            marginBottom: 32,
            paddingBottom: 24,
            borderBottom: '1px solid #eee',
          }}
        >
          <div>
            <Link
              to="/"
              style={{
                fontSize: 14,
                color: '#666',
                textDecoration: 'none',
                marginBottom: 8,
                display: 'inline-block',
              }}
            >
              GymGM
            </Link>
            <h1
              style={{
                fontSize: 32,
                fontWeight: 700,
                color: '#1a1a1a',
                margin: 0,
                letterSpacing: '-0.02em',
              }}
            >
              {title}
            </h1>
          </div>
          <Link
            to={`/${config.path}/${otherLang}`}
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#1a1a1a',
              textDecoration: 'none',
              padding: '8px 16px',
              border: '2px solid #1a1a1a',
              borderRadius: 8,
            }}
          >
            {otherLabel}
          </Link>
        </header>

        {loading && (
          <p style={{ color: '#666', fontSize: 16 }}>
            {lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}
          </p>
        )}

        {error && (
          <p style={{ color: '#d32f2f', fontSize: 16 }}>{error}</p>
        )}

        {!loading && !error && content && (
          <div
            style={{
              fontSize: 15,
              lineHeight: 1.7,
              color: '#333',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {content}
          </div>
        )}

        <footer
          style={{
            marginTop: 40,
            paddingTop: 24,
            borderTop: '1px solid #eee',
            fontSize: 14,
            color: '#666',
          }}
        >
          <p style={{ margin: '0 0 8px' }}>
            {lang === 'tr' ? 'İletişim:' : 'Contact:'}{' '}
            <a href="mailto:info@gymgm.com" style={{ color: '#1a1a1a' }}>
              info@gymgm.com
            </a>
          </p>
          <p style={{ margin: 0 }}>
            <Link to={`/privacy-policy/${lang}`} style={{ color: '#666', marginRight: 16 }}>
              {DOCUMENT_CONFIG.privacy.titles[lang]}
            </Link>
            <Link to={`/terms-of-service/${lang}`} style={{ color: '#666' }}>
              {DOCUMENT_CONFIG.terms.titles[lang]}
            </Link>
          </p>
        </footer>
      </article>
    </div>
  );
}

export default LegalDocumentPage;
